import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { chatbotAPI, authAPI, handleAPIError, getStatusColor, formatDate } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getPlanIdToNameMap } from '../config/stripe';
import { 
  X,
  Building,
  Globe,
  Palette,
  MessageCircle,
  HelpCircle,
  Calendar,
  User,
  Trash2,
  UserCheck,
  Download,
  FileText,
  Phone,
  Monitor,
  Settings,
  Eye,
  Upload
} from 'lucide-react';

const ChatbotDetailsModal = ({ chatbot, isOpen, onClose, onUpdate, onDelete }) => {
  const { user, isAdmin, isEmployee } = useAuth();
  const { toast } = useToast();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // new state for tabs
  const [dashboardPolicies, setDashboardPolicies] = useState(''); // state for dashboard policies
  const [showTestingModal, setShowTestingModal] = useState(false);
  const [testingData, setTestingData] = useState({
    fastbots_url: '',
    add_to_website_code: '',
    display_inside_webpage_code: ''
  });
  const [showLogoPreview, setShowLogoPreview] = useState(false);
  const [planIdToName, setPlanIdToName] = useState({});

  // Load plan ID to name mapping
  useEffect(() => {
    const loadPlanMapping = async () => {
      try {
        const mapping = await getPlanIdToNameMap();
        setPlanIdToName(mapping);
      } catch (error) {
        console.error('Error loading plan ID mapping:', error);
        // Fallback to empty object
        setPlanIdToName({});
      }
    };
    
    loadPlanMapping();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      if ((isAdmin() || isEmployee()) && isOpen) {
        try {
          const response = await authAPI.getEmployees();
          setEmployees(response.data);
        } catch (error) {
          console.error('Error fetching employees:', error);
        }
      }
    };
    
    fetchEmployees();
  }, [isOpen, isAdmin, isEmployee]);

  const handleDelete = async () => {
    // Only admin and chatbot owners can delete
    if (!isAdmin() && chatbot.customer_id !== user.id) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to delete this chatbot",
        variant: "destructive",
      });
      return;
    }

    if (window.confirm(`Are you sure you want to delete the chatbot for ${chatbot.company_name}? This action cannot be undone.`)) {
      try {
        setLoading(true);
        await chatbotAPI.deleteChatbot(chatbot.id);
        
        toast({
          title: "Chatbot deleted",
          description: "The chatbot has been deleted successfully",
        });
        
        if (onDelete) onDelete(chatbot.id);
        onClose();
      } catch (error) {
        const errorMessage = handleAPIError(error, 'Failed to delete chatbot');
        toast({
          title: "Delete failed",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownloadLogo = async (filename) => {
    try {
      setLoading(true);
      const response = await chatbotAPI.downloadLogo(chatbot.id);
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Logo downloaded",
        description: `${filename} has been downloaded successfully`,
      });
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to download logo');
      toast({
        title: "Download failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      setLoading(true);
      const response = await chatbotAPI.downloadFile(chatbot.id, fileId);
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "File downloaded",
        description: `${filename} has been downloaded successfully`,
      });
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to download file');
      toast({
        title: "Download failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setLoading(true);
    try {
      if (newStatus === 'final_payment') {
        await chatbotAPI.updateChatbotToFinalPayment(chatbot.id);
      } else {
        await chatbotAPI.updateChatbotStatus(chatbot.id, newStatus);
      }
      toast({
        title: "Status Updated",
        description: `Chatbot status updated to ${newStatus.replace('_', ' ')}`,
      });
      onUpdate();
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Status Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestingPhaseUpdate = async () => {
    if (!testingData.fastbots_url || !testingData.add_to_website_code || !testingData.display_inside_webpage_code) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      await chatbotAPI.updateChatbotToTesting(chatbot.id, testingData);
      toast({
        title: "Moved to Testing Phase",
        description: "Chatbot has been moved to testing phase successfully",
      });
      setShowTestingModal(false);
      onUpdate();
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Testing Phase Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-600/20 text-yellow-300 border-yellow-500/30',
      in_progress: 'bg-blue-600/20 text-blue-300 border-blue-500/30',
      testing: 'bg-purple-600/20 text-purple-300 border-purple-500/30',
      final_payment: 'bg-yellow-600/20 text-yellow-300 border-yellow-500/30',
      deploy: 'bg-green-600/20 text-green-300 border-green-500/30',
      done: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30',
      completed: 'bg-green-600/20 text-green-300 border-green-500/30',
      deployed: 'bg-purple-600/20 text-purple-300 border-purple-500/30',
    };
    return colors[status] || 'bg-gray-600/20 text-gray-300 border-gray-500/30';
  };

  const handleReassign = async (newEmployeeId) => {
    // Only admin can reassign
    if (!isAdmin()) {
      toast({
        title: "Permission Denied",
        description: "Only admin can reassign chatbots",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      await chatbotAPI.reassignChatbot(chatbot.id, newEmployeeId);
      
      toast({
        title: "Chatbot reassigned",
        description: "The chatbot has been reassigned successfully",
      });
      
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to reassign chatbot');
      toast({
        title: "Reassignment failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDashboardPolicies = async () => {
    if (!dashboardPolicies.trim()) {
      toast({
        title: "No policies to save",
        description: "Please enter dashboard policies before saving",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // For now, we'll just show a success message
      // In the future, this would save to the backend
      toast({
        title: "Dashboard policies saved",
        description: "Dashboard policies have been saved successfully",
      });
      
      console.log('Dashboard policies saved:', {
        chatbotId: chatbot.id,
        policies: dashboardPolicies
      });
      
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to save dashboard policies');
      toast({
        title: "Save failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if current user can view this chatbot
  const canView = isAdmin() || 
                  chatbot.customer_id === user.id || 
                  (isEmployee() && chatbot.assigned_employee_id === user.id);

  if (!isOpen || !chatbot) return null;

  if (!canView) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-900 border-violet-500/20">
          <CardContent className="p-8 text-center">
            <h3 className="text-white text-lg font-semibold mb-2">Access Denied</h3>
            <p className="text-gray-400 mb-4">You don't have permission to view this chatbot's details.</p>
            <Button onClick={onClose} className="bg-violet-600 hover:bg-violet-700">
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-violet-500/20">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-white flex items-center">
                <Building className="w-8 h-8 mr-3 text-violet-400" />
                Chatbot Details
                {(isAdmin() || isEmployee()) && chatbot.plan_type && (
                  <Badge className="ml-4 bg-green-600 text-white">
                    {(() => {
                      // Try to get human-readable plan name first
                      const planId = chatbot.selected_plan_id || chatbot.selected_price_id;
                      const planName = planId ? planIdToName[planId] : null;
                      
                      if (planName) {
                        return planName;
                      }
                      
                      // Fallback to plan type + billing cycle
                      const typeCapitalized = chatbot.plan_type === 'starter' ? 'Starter' :
                                             chatbot.plan_type === 'professional' ? 'Professional' :
                                             chatbot.plan_type === 'enterprise' ? 'Enterprise' : 
                                             chatbot.plan_type.charAt(0).toUpperCase() + chatbot.plan_type.slice(1);
                      const cycle = chatbot.billing_cycle ? ` ${chatbot.billing_cycle.charAt(0).toUpperCase() + chatbot.billing_cycle.slice(1)}` : '';
                      return `${typeCapitalized}${cycle}`;
                    })()}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                Complete information for {chatbot.company_name}
              </CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        {/* Tab Navigation */}
        <div className="border-b border-gray-700">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-violet-500 text-violet-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Details
            </button>
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-violet-500 text-violet-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <Monitor className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-violet-500 text-violet-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
          </nav>
        </div>

        <CardContent className="p-6">
          {/* Tab Content */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Company Information */}
              <Card className="bg-gray-800 border-gray-600">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Building className="w-5 h-5 mr-2 text-blue-400" />
                    Company Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm">Company Name</label>
                    <p className="text-white font-medium">{chatbot.company_name}</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Website</label>
                    <p className="text-white font-medium">{chatbot.company_website || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Status</label>
                    <Badge className={`${getStatusColor(chatbot.status)} border-current`}>
                      {chatbot.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Created</label>
                    <p className="text-white font-medium flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(chatbot.created_at)}
                    </p>
                  </div>
                  
                  {/* Customer Information - Only visible to admin and employees */}
                  {(isAdmin() || isEmployee()) && (
                    <div className="pt-4 border-t border-gray-700">
                      <h4 className="text-white font-semibold mb-3 flex items-center">
                        <User className="w-4 h-4 mr-2 text-green-400" />
                        Customer Information
                      </h4>
                      <div className="space-y-2 pl-6">
                        <div>
                          <label className="text-gray-400 text-sm">Customer ID</label>
                          <p className="text-white font-medium font-mono">{chatbot.customer_id}</p>
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm">Email</label>
                          <p className="text-white font-medium">{chatbot.user_email}</p>
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm">Payment Status</label>
                          <Badge className={chatbot.deposit_paid ? 'bg-green-600' : 'bg-red-600'}>
                            {chatbot.deposit_paid ? 'Deposit Paid' : 'Pending Payment'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Company Logo */}
              {chatbot.company_logo && (
                <Card className="bg-gray-800 border-gray-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center">
                      <Building className="w-5 h-5 mr-2 text-yellow-400" />
                      Company Logo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                          <Building className="w-6 h-6 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{chatbot.company_logo.filename}</p>
                          <p className="text-gray-400 text-sm">
                            {(chatbot.company_logo.file_size / 1024).toFixed(1)} KB • {chatbot.company_logo.content_type}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => setShowLogoPreview(true)}
                          size="sm"
                          variant="outline"
                          className="text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        {(isAdmin() || isEmployee()) && (
                          <Button
                            onClick={() => handleDownloadLogo(chatbot.company_logo.filename)}
                            size="sm" 
                            variant="outline"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Chatbot Configuration */}
              <Card className="bg-gray-800 border-gray-600">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <MessageCircle className="w-5 h-5 mr-2 text-green-400" />
                    Chatbot Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm">Personality</label>
                    <div className="space-y-2">
                      <p className="text-white font-medium capitalize">
                        {chatbot.customization?.personality || 'professional'}
                      </p>
                      {chatbot.customization?.personality === 'custom' && chatbot.customization?.custom_personality && (
                        <div className="mt-2 p-3 bg-gray-700/50 rounded-lg border border-violet-500/20">
                          <label className="text-gray-400 text-xs">Custom Description:</label>
                          <p className="text-gray-300 text-sm mt-1 italic">
                            "{chatbot.customization.custom_personality}"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Greeting Message</label>
                    <p className="text-white">"{chatbot.customization?.greeting_message || 'Hello! How can I help you today?'}"</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Color Configuration</label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-8 h-8 rounded border border-gray-600" 
                          style={{ backgroundColor: chatbot.customization?.chat_window_background || '#1f2937' }}
                        ></div>
                        <div className="flex flex-col">
                          <span className="text-gray-300 text-sm">Chat Window</span>
                          <span className="text-xs text-violet-400 font-mono">
                            {chatbot.customization?.chat_window_background || '#1f2937'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-8 h-8 rounded border border-gray-600" 
                          style={{ backgroundColor: chatbot.customization?.user_message_background || '#8b5cf6' }}
                        ></div>
                        <div className="flex flex-col">
                          <span className="text-gray-300 text-sm">User Messages</span>
                          <span className="text-xs text-violet-400 font-mono">
                            {chatbot.customization?.user_message_background || '#8b5cf6'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-8 h-8 rounded border border-gray-600" 
                          style={{ backgroundColor: chatbot.customization?.live_message_background || '#10b981' }}
                        ></div>
                        <div className="flex flex-col">
                          <span className="text-gray-300 text-sm">Live Messages</span>
                          <span className="text-xs text-violet-400 font-mono">
                            {chatbot.customization?.live_message_background || '#10b981'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-8 h-8 rounded border border-gray-600" 
                          style={{ backgroundColor: chatbot.customization?.chatbot_message_background || '#374151' }}
                        ></div>
                        <div className="flex flex-col">
                          <span className="text-gray-300 text-sm">Bot Messages</span>
                          <span className="text-xs text-violet-400 font-mono">
                            {chatbot.customization?.chatbot_message_background || '#374151'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-8 h-8 rounded border border-gray-600" 
                          style={{ backgroundColor: chatbot.customization?.thinking_dots_color || '#a78bfa' }}
                        ></div>
                        <div className="flex flex-col">
                          <span className="text-gray-300 text-sm">Thinking Dots</span>
                          <span className="text-xs text-violet-400 font-mono">
                            {chatbot.customization?.thinking_dots_color || '#a78bfa'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-8 h-8 rounded border border-gray-600" 
                          style={{ backgroundColor: chatbot.customization?.send_button_color || '#7c3aed' }}
                        ></div>
                        <div className="flex flex-col">
                          <span className="text-gray-300 text-sm">Send Button</span>
                          <span className="text-xs text-violet-400 font-mono">
                            {chatbot.customization?.send_button_color || '#7c3aed'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">LLM Model</label>
                    <p className="text-white font-medium">{chatbot.advanced_settings?.llm_model || 'gpt-4o-mini'}</p>
                  </div>
                  {chatbot.advanced_settings?.rate_limiting?.enabled && (
                    <div>
                      <label className="text-gray-400 text-sm">Rate Limiting</label>
                      <p className="text-white">
                        {chatbot.advanced_settings.rate_limiting.message_limit} messages per {chatbot.advanced_settings.rate_limiting.time_period} minutes
                      </p>
                      {chatbot.advanced_settings.rate_limiting.limit_message && (
                        <p className="text-gray-400 text-sm italic">"{chatbot.advanced_settings.rate_limiting.limit_message}"</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Advanced Settings */}
              {chatbot.advanced_settings && (isAdmin() || isEmployee()) && (
                <Card className="bg-gray-800 border-gray-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center">
                      <Settings className="w-5 h-5 mr-2 text-gray-400" />
                      Advanced Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-gray-400 text-sm">Placeholder Text</label>
                      <p className="text-white">{chatbot.advanced_settings.placeholder_text || 'Type your message...'}</p>
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">Welcome Message</label>
                      <p className="text-white">{chatbot.advanced_settings.welcome_message || 'Hi! I\'m here to help you.'}</p>
                    </div>
                    {chatbot.advanced_settings.suggestions && chatbot.advanced_settings.suggestions.length > 0 && (
                      <div>
                        <label className="text-gray-400 text-sm">Suggestions</label>
                        <div className="mt-2 space-y-1">
                          {chatbot.advanced_settings.suggestions.map((suggestion, index) => (
                            <div key={index} className="p-2 bg-blue-900/20 rounded border border-blue-500/20">
                              <p className="text-blue-300 text-sm">"{suggestion}"</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {chatbot.advanced_settings.domain_whitelist && chatbot.advanced_settings.domain_whitelist.enabled && (
                      <div>
                        <label className="text-gray-400 text-sm">Domain Whitelist</label>
                        <div className="mt-2">
                          {chatbot.advanced_settings.domain_whitelist.domains && chatbot.advanced_settings.domain_whitelist.domains.length > 0 ? (
                            <div className="space-y-1">
                              {chatbot.advanced_settings.domain_whitelist.domains.map((domain, index) => (
                                <div key={index} className="p-2 bg-green-900/20 rounded border border-green-500/20">
                                  <p className="text-green-300 text-sm font-mono">{domain}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-yellow-400 text-sm italic">No domains configured</p>
                          )}
                        </div>
                      </div>
                    )}

                  {/* FAQs */}
                  {chatbot.faqs && chatbot.faqs.length > 0 && (
                    <Card className="bg-gray-800 border-gray-600">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center">
                          <HelpCircle className="w-5 h-5 mr-2 text-cyan-400" />
                          Frequently Asked Questions ({chatbot.faqs.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {chatbot.faqs.map((faq, index) => (
                            <div key={index} className="border-l-4 border-cyan-500 pl-4">
                              <h4 className="text-white font-medium mb-2">{faq.question}</h4>
                              <p className="text-gray-300 text-sm">{faq.answer}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Additional Information */}
                  {chatbot.additional_info && (
                    <Card className="bg-gray-800 border-gray-600">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center">
                          <FileText className="w-5 h-5 mr-2 text-indigo-400" />
                          Additional Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-300 whitespace-pre-wrap">{chatbot.additional_info}</p>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

          {/* Additional Files Section - ONLY IN DETAILS TAB */}
          {activeTab === 'details' && chatbot.additional_files && chatbot.additional_files.length > 0 && (isAdmin() || isEmployee()) && (
            <Card className="bg-gray-800 border-gray-600 mt-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-400" />
                  Additional Files ({chatbot.additional_files.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {chatbot.additional_files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="text-white font-medium">{file.filename}</p>
                          <p className="text-gray-400 text-sm">
                            {(file.file_size / 1024).toFixed(1)} KB • {file.content_type}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDownloadFile(file.id, file.filename)}
                        size="sm"
                        variant="outline"
                        className="text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contact Preferences Section - ONLY IN DETAILS TAB */}
          {activeTab === 'details' && chatbot.contact_info && (isAdmin() || isEmployee()) && (
            <Card className="bg-gray-800 border-gray-600 mt-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Phone className="w-5 h-5 mr-2 text-green-400" />
                  Contact Preferences
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-400 text-sm">Contact Method</label>
                    <p className="text-white font-medium capitalize">
                      {chatbot.contact_info.contact_method?.replace('_', ' ') || 'Built-in Chat'}
                    </p>
                  </div>
                  {chatbot.contact_info.whatsapp_number && (
                    <div>
                      <label className="text-gray-400 text-sm">WhatsApp Number</label>
                      <p className="text-white font-medium">{chatbot.contact_info.whatsapp_number}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assignment & Actions Section - ONLY IN DETAILS TAB */}
          {activeTab === 'details' && (isAdmin() || (isEmployee() && chatbot.assigned_employee_id === user?.id)) && (
            <Card className="bg-gray-800 border-gray-600 mt-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <User className="w-5 h-5 mr-2 text-orange-400" />
                  {isAdmin() ? 'Assignment & Actions' : 'Assigned Actions'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm">Currently Assigned To</label>
                  <p className="text-white font-medium">
                    {chatbot.assigned_employee_id ? 
                      employees.find(e => e.id === chatbot.assigned_employee_id)?.first_name + ' ' + 
                      employees.find(e => e.id === chatbot.assigned_employee_id)?.last_name || 'Unknown Employee'
                      : 'Unassigned'
                    }
                  </p>
                </div>
                
                {/* Only admins can reassign */}
                {isAdmin() && employees.length > 0 && (
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Reassign To</label>
                    <div className="flex flex-wrap gap-2">
                      {employees.map((employee) => (
                        <Button
                          key={employee.id}
                          onClick={() => handleReassign(employee.id)}
                          disabled={loading || employee.id === chatbot.assigned_employee_id}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          {employee.first_name} {employee.last_name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Status Workflow Management */}
                <div className="pt-4 border-t border-gray-700">
                  <label className="text-gray-400 text-sm mb-3 block">Status Workflow Management</label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-medium">Current Status:</span>
                      <Badge className={`${getStatusColor(chatbot.status)} border-current`}>
                        {chatbot.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    {/* Status-specific actions */}
                    <div className="flex flex-wrap gap-2">
                      {/* Move to Testing Phase */}
                      {(chatbot.status === 'in_progress') && (
                        <Button
                          onClick={() => setShowTestingModal(true)}
                          disabled={loading}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Move to Testing Phase
                        </Button>
                      )}
                      
                      {/* Move to Final Payment */}
                      {(chatbot.status === 'testing') && (
                        <Button
                          onClick={() => handleStatusUpdate('final_payment')}
                          disabled={loading}
                          size="sm"
                          className="bg-yellow-600 hover:bg-yellow-700"
                        >
                          Move to Final Payment
                        </Button>
                      )}
                      
                      {/* Move to Deploy */}
                      {(chatbot.status === 'final_payment' && chatbot.final_payment_paid) && (
                        <Button
                          onClick={() => handleStatusUpdate('deploy')}
                          disabled={loading}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Move to Deploy Phase
                        </Button>
                      )}
                      
                      {/* Move to Done */}
                      {(chatbot.status === 'deploy') && (
                        <Button
                          onClick={() => handleStatusUpdate('done')}
                          disabled={loading}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          Mark as Completed
                        </Button>
                      )}
                    </div>
                    
                    {/* Display testing phase data if available */}
                    {chatbot.fastbots_url && (
                      <div className="mt-4 p-3 bg-gray-700/50 rounded-lg border border-blue-500/20">
                        <h4 className="text-white font-medium mb-2">Testing Phase Data:</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-gray-400">BrixBot URL:</span>
                            <a 
                              href={chatbot.fastbots_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 ml-2"
                            >
                              {chatbot.fastbots_url}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Display plan selection if available */}
                    {chatbot.selected_plan_id && (
                      <div className="mt-4 p-3 bg-gray-700/50 rounded-lg border border-green-500/20">
                        <h4 className="text-white font-medium mb-2">Selected Plan:</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-gray-400">Plan ID:</span>
                            <span className="text-white ml-2">{chatbot.selected_plan_id}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Plan Type:</span>
                            <span className="text-white ml-2 capitalize">{chatbot.plan_type}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-700">
                  <Button
                    onClick={handleDelete}
                    disabled={loading}
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Chatbot
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dashboard Tab Content */}
          {activeTab === 'dashboard' && (
            <DashboardTabContent chatbot={chatbot} isAdmin={isAdmin} isEmployee={isEmployee} user={user} planIdToName={planIdToName} />
          )}

          {/* Settings Tab Content */}
          {activeTab === 'settings' && (
            <div className="p-6">
              <Card className="bg-gray-800 border-gray-600">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Settings className="w-5 h-5 mr-2 text-gray-400" />
                    Settings
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Advanced chatbot settings and configuration
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <Settings className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                    <h3 className="text-xl text-white font-semibold mb-2">Coming Soon</h3>
                    <p className="text-gray-400 mb-4">
                      Advanced settings and configuration options will be available in a future update.
                    </p>
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span>Expected release: Q3 2025</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Footer - Always visible */}
          <div className="mt-6 pt-6 border-t border-gray-700 flex justify-end px-6">
            <Button onClick={onClose} className="bg-violet-600 hover:bg-violet-700">
              Close
            </Button>
          </div>

        </CardContent>
      </Card>

      {/* Testing Phase Modal */}
      {showTestingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-60 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl bg-gray-900 border-violet-500/20">
            <CardHeader className="border-b border-gray-700">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-white">
                  Move to Testing Phase
                </CardTitle>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowTestingModal(false)} 
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm block mb-2">
                    BrixBot URL <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    placeholder="https://app.brixbot.ai/embed/..."
                    value={testingData.fastbots_url}
                    onChange={(e) => setTestingData({...testingData, fastbots_url: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-600 text-white rounded px-3 py-2 focus:border-violet-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm block mb-2">
                    Add to Website Code <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    placeholder="<script>...</script>"
                    value={testingData.add_to_website_code}
                    onChange={(e) => setTestingData({...testingData, add_to_website_code: e.target.value})}
                    rows={4}
                    className="w-full bg-gray-800 border border-gray-600 text-white rounded px-3 py-2 focus:border-violet-500 focus:outline-none font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm block mb-2">
                    Display Inside Webpage Code <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    placeholder="<iframe>...</iframe>"
                    value={testingData.display_inside_webpage_code}
                    onChange={(e) => setTestingData({...testingData, display_inside_webpage_code: e.target.value})}
                    rows={4}
                    className="w-full bg-gray-800 border border-gray-600 text-white rounded px-3 py-2 focus:border-violet-500 focus:outline-none font-mono text-sm"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  variant="ghost"
                  onClick={() => setShowTestingModal(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTestingPhaseUpdate}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Updating...' : 'Move to Testing'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Dashboard Tab Component with Real Data Integration
const DashboardTabContent = ({ chatbot, isAdmin, isEmployee, user, planIdToName }) => {
  const { toast } = useToast();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Form states for admin/employee editing
  const [messageUsage, setMessageUsage] = useState(0);
  const [todaysUsers, setTodaysUsers] = useState(0);
  const [todaysMessages, setTodaysMessages] = useState(0);
  const [newCountry, setNewCountry] = useState('');
  const [newChats, setNewChats] = useState('');

  // Chat history upload states
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Handle chat history file upload
  const handleChatHistoryUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "Error",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    setUploadingFile(true);
    try {
      await chatbotAPI.uploadChatHistory(chatbot.id, selectedFile);
      
      // Reload dashboard data to show new file
      const response = await chatbotAPI.getDashboard(chatbot.id);
      setDashboardData(response.data);
      
      // Clear file selection
      setSelectedFile(null);
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
      toast({
        title: "Success",
        description: "Chat history file uploaded successfully"
      });
    } catch (error) {
      console.error('Failed to upload chat history:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload chat history file",
        variant: "destructive"
      });
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle chat history file download
  const handleChatHistoryDownload = async (filename, originalFilename) => {
    try {
      const response = await chatbotAPI.downloadChatHistory(chatbot.id, filename);
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: "Chat history file downloaded successfully"
      });
    } catch (error) {
      console.error('Failed to download chat history:', error);
      toast({
        title: "Error",
        description: "Failed to download chat history file",
        variant: "destructive"
      });
    }
  };

  // Handle chat history file deletion (admin/employee)
  const handleChatHistoryDelete = async (filename, originalFilename) => {
    try {
      await chatbotAPI.deleteChatHistory(chatbot.id, filename);
      
      // Reload dashboard data to remove deleted file
      const response = await chatbotAPI.getDashboard(chatbot.id);
      setDashboardData(response.data);
      
      toast({
        title: "Success",
        description: "Chat history file deleted successfully"
      });
    } catch (error) {
      console.error('Failed to delete chat history:', error);
      toast({
        title: "Error",
        description: "Failed to delete chat history file",
        variant: "destructive"
      });
    }
  };

  // Calculate dynamic plan quota based on current chatbot plan (same logic as plan name display)
  const getCurrentPlanQuota = () => {
    // Try to get human-readable plan name first (same as pricing plan logic)
    const planId = chatbot.selected_plan_id || chatbot.selected_price_id;
    const planName = planId ? planIdToName[planId] : null;
    
    if (planName) {
      // Extract plan type from plan name
      if (planName.toLowerCase().includes('starter')) return 1000;
      if (planName.toLowerCase().includes('professional')) return 1500;
      if (planName.toLowerCase().includes('enterprise')) return 3000;
    }
    
    // Fallback to plan_type
    if (chatbot.plan_type) {
      switch (chatbot.plan_type.toLowerCase()) {
        case 'starter': return 1000;
        case 'professional': return 1500;
        case 'enterprise': return 3000;
        default: return 1000;
      }
    }
    
    return 1000; // default
  };

  // Check if current plan supports chat history (same logic as pricing plan logic)
  const supportsChatHistory = () => {
    // Try to get human-readable plan name first (same as pricing plan logic)
    const planId = chatbot.selected_plan_id || chatbot.selected_price_id;
    const planName = planId ? planIdToName[planId] : null;
    
    if (planName) {
      // Check if plan name includes Professional or Enterprise
      const lowerPlanName = planName.toLowerCase();
      return lowerPlanName.includes('professional') || lowerPlanName.includes('enterprise');
    }
    
    // Fallback to plan_type
    if (chatbot.plan_type) {
      return ['professional', 'enterprise'].includes(chatbot.plan_type.toLowerCase());
    }
    
    return false; // default
  };

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const response = await chatbotAPI.getDashboard(chatbot.id);
        setDashboardData(response.data);
        
        // Set form values from existing data
        setMessageUsage(response.data.message_usage || 0);
        setTodaysUsers(response.data.todays_users || 0);
        setTodaysMessages(response.data.todays_messages || 0);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [chatbot.id]);

  // Update dashboard data
  const updateDashboard = async (updateData) => {
    setUpdating(true);
    try {
      await chatbotAPI.updateDashboard(chatbot.id, updateData);
      
      // Reload dashboard data
      const response = await chatbotAPI.getDashboard(chatbot.id);
      setDashboardData(response.data);
      
      toast({
        title: "Success",
        description: "Dashboard updated successfully"
      });
    } catch (error) {
      console.error('Failed to update dashboard:', error);
      toast({
        title: "Error", 
        description: "Failed to update dashboard",
        variant: "destructive"
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-white">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Customer View - Show real dashboard data */}
      {!isAdmin() && !isEmployee() && (
        <div className="space-y-6">
          <Card className="bg-gray-800 border-gray-600">
            <CardHeader>
              <CardTitle className="text-white">Popular Countries (Last 28 days)</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.popular_countries?.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.popular_countries.map((country, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <span className="text-white font-medium">{country.name}</span>
                      <span className="text-violet-400">{country.chats} chats</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">Coming soon...</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-600">
            <CardHeader>
              <CardTitle className="text-white">Message Usage (Last 28 days)</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.has_data ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Messages Used</span>
                    <span className="text-white font-medium">
                      {(dashboardData.message_usage || 0).toLocaleString()} / {getCurrentPlanQuota().toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <div 
                      className="bg-violet-500 h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(((dashboardData.message_usage || 0) / getCurrentPlanQuota()) * 100, 100)}%`
                      }}
                    />
                  </div>
                  <div className="text-sm text-gray-400">
                    {Math.max(getCurrentPlanQuota() - (dashboardData.message_usage || 0), 0).toLocaleString()} messages remaining
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">Coming soon...</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-600">
            <CardHeader>
              <CardTitle className="text-white">Today's Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.has_data ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-700 rounded">
                    <div className="text-2xl font-bold text-white">{dashboardData.todays_users}</div>
                    <div className="text-gray-400 text-sm">Users</div>
                  </div>
                  <div className="text-center p-4 bg-gray-700 rounded">
                    <div className="text-2xl font-bold text-white">{dashboardData.todays_messages}</div>
                    <div className="text-gray-400 text-sm">Messages</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">Coming soon...</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chat History Section - Only for Professional/Enterprise plans */}
          {supportsChatHistory() && (
            <Card className="bg-gray-800 border-gray-600">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <MessageCircle className="w-5 h-5 mr-2 text-purple-400" />
                  Chat History
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Download conversation logs and chat transcripts
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData?.conversation_files?.length > 0 ? (
                  <div className="space-y-2">
                    <h4 className="text-white font-medium mb-3">Available Downloads</h4>
                    {dashboardData.conversation_files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded">
                        <div>
                          <div className="text-white font-medium">{file.filename}</div>
                          <div className="text-gray-400 text-sm">
                            Uploaded: {new Date(file.upload_date).toLocaleDateString()} • {(file.file_size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white"
                          onClick={() => handleChatHistoryDownload(file.stored_filename, file.filename)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                    <p className="text-gray-400 mb-2">No chat history available yet</p>
                    <p className="text-gray-500 text-sm">
                      Chat conversation files will appear here once uploaded by your support team
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Admin/Employee View - Editable dashboard fields */}
      {(isAdmin() || isEmployee()) && (
        <div className="space-y-6">
          {/* Chat History Upload Section - Only for Professional/Enterprise plans */}
          {supportsChatHistory() && (
            <Card className="bg-gray-800 border-gray-600">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <MessageCircle className="w-5 h-5 mr-2 text-purple-400" />
                  Chat History Management
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Upload conversation logs for {chatbot.plan_type} plan customers to download
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm block mb-2">Upload Conversation Log</label>
                    <input
                      type="file"
                      accept=".txt,.csv,.json,.pdf"
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                    />
                    {selectedFile && (
                      <p className="text-gray-400 text-sm mt-2">
                        Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={handleChatHistoryUpload}
                    disabled={!selectedFile || uploadingFile}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {uploadingFile ? 'Uploading...' : 'Upload Chat History'}
                  </Button>
                  
                  {/* Show existing conversation files for management */}
                  {dashboardData?.conversation_files?.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-white font-medium mb-3">Uploaded Files</h4>
                      <div className="space-y-2">
                        {dashboardData.conversation_files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded">
                            <div className="flex items-center space-x-3">
                              <FileText className="w-5 h-5 text-purple-400" />
                              <div>
                                <div className="text-white font-medium">{file.filename}</div>
                                <div className="text-gray-400 text-sm">
                                  {new Date(file.upload_date).toLocaleDateString()} • {(file.file_size / 1024).toFixed(1)} KB
                                </div>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleChatHistoryDownload(file.stored_filename, file.filename)}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                                onClick={() => handleChatHistoryDelete(file.stored_filename, file.filename)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="bg-gray-800 border-gray-600">
            <CardHeader>
              <CardTitle className="text-white">Popular Countries (Last 28 days)</CardTitle>
              <CardDescription className="text-gray-400">
                Manage countries and chat numbers displayed to customers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Current countries */}
                {dashboardData?.popular_countries?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-white font-medium">Current Countries</h4>
                    {dashboardData.popular_countries.map((country, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-700 rounded">
                        <span className="text-white">{country.name}: {country.chats} chats</span>
                        <Button size="sm" variant="outline" onClick={() => {
                          const updated = dashboardData.popular_countries.filter((_, i) => i !== index);
                          updateDashboard({ popular_countries: updated });
                        }}>
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add new country */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm block mb-2">Country</label>
                    <select 
                      value={newCountry} 
                      onChange={(e) => setNewCountry(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2"
                    >
                      <option value="">Select Country</option>
                      <option value="Afghanistan">Afghanistan</option>
                      <option value="Albania">Albania</option>
                      <option value="Algeria">Algeria</option>
                      <option value="Andorra">Andorra</option>
                      <option value="Angola">Angola</option>
                      <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                      <option value="Argentina">Argentina</option>
                      <option value="Armenia">Armenia</option>
                      <option value="Australia">Australia</option>
                      <option value="Austria">Austria</option>
                      <option value="Azerbaijan">Azerbaijan</option>
                      <option value="Bahamas">Bahamas</option>
                      <option value="Bahrain">Bahrain</option>
                      <option value="Bangladesh">Bangladesh</option>
                      <option value="Barbados">Barbados</option>
                      <option value="Belarus">Belarus</option>
                      <option value="Belgium">Belgium</option>
                      <option value="Belize">Belize</option>
                      <option value="Benin">Benin</option>
                      <option value="Bhutan">Bhutan</option>
                      <option value="Bolivia">Bolivia</option>
                      <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                      <option value="Botswana">Botswana</option>
                      <option value="Brazil">Brazil</option>
                      <option value="Brunei">Brunei</option>
                      <option value="Bulgaria">Bulgaria</option>
                      <option value="Burkina Faso">Burkina Faso</option>
                      <option value="Burundi">Burundi</option>
                      <option value="Cabo Verde">Cabo Verde</option>
                      <option value="Cambodia">Cambodia</option>
                      <option value="Cameroon">Cameroon</option>
                      <option value="Canada">Canada</option>
                      <option value="Central African Republic">Central African Republic</option>
                      <option value="Chad">Chad</option>
                      <option value="Chile">Chile</option>
                      <option value="China">China</option>
                      <option value="Colombia">Colombia</option>
                      <option value="Comoros">Comoros</option>
                      <option value="Congo">Congo</option>
                      <option value="Costa Rica">Costa Rica</option>
                      <option value="Croatia">Croatia</option>
                      <option value="Cuba">Cuba</option>
                      <option value="Cyprus">Cyprus</option>
                      <option value="Czech Republic">Czech Republic</option>
                      <option value="Democratic Republic of the Congo">Democratic Republic of the Congo</option>
                      <option value="Denmark">Denmark</option>
                      <option value="Djibouti">Djibouti</option>
                      <option value="Dominica">Dominica</option>
                      <option value="Dominican Republic">Dominican Republic</option>
                      <option value="Ecuador">Ecuador</option>
                      <option value="Egypt">Egypt</option>
                      <option value="El Salvador">El Salvador</option>
                      <option value="Equatorial Guinea">Equatorial Guinea</option>
                      <option value="Eritrea">Eritrea</option>
                      <option value="Estonia">Estonia</option>
                      <option value="Eswatini">Eswatini</option>
                      <option value="Ethiopia">Ethiopia</option>
                      <option value="Fiji">Fiji</option>
                      <option value="Finland">Finland</option>
                      <option value="France">France</option>
                      <option value="Gabon">Gabon</option>
                      <option value="Gambia">Gambia</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Germany">Germany</option>
                      <option value="Ghana">Ghana</option>
                      <option value="Greece">Greece</option>
                      <option value="Grenada">Grenada</option>
                      <option value="Guatemala">Guatemala</option>
                      <option value="Guinea">Guinea</option>
                      <option value="Guinea-Bissau">Guinea-Bissau</option>
                      <option value="Guyana">Guyana</option>
                      <option value="Haiti">Haiti</option>
                      <option value="Honduras">Honduras</option>
                      <option value="Hungary">Hungary</option>
                      <option value="Iceland">Iceland</option>
                      <option value="India">India</option>
                      <option value="Indonesia">Indonesia</option>
                      <option value="Iran">Iran</option>
                      <option value="Iraq">Iraq</option>
                      <option value="Ireland">Ireland</option>
                      <option value="Israel">Israel</option>
                      <option value="Italy">Italy</option>
                      <option value="Ivory Coast">Ivory Coast</option>
                      <option value="Jamaica">Jamaica</option>
                      <option value="Japan">Japan</option>
                      <option value="Jordan">Jordan</option>
                      <option value="Kazakhstan">Kazakhstan</option>
                      <option value="Kenya">Kenya</option>
                      <option value="Kiribati">Kiribati</option>
                      <option value="Kuwait">Kuwait</option>
                      <option value="Kyrgyzstan">Kyrgyzstan</option>
                      <option value="Laos">Laos</option>
                      <option value="Latvia">Latvia</option>
                      <option value="Lebanon">Lebanon</option>
                      <option value="Lesotho">Lesotho</option>
                      <option value="Liberia">Liberia</option>
                      <option value="Libya">Libya</option>
                      <option value="Liechtenstein">Liechtenstein</option>
                      <option value="Lithuania">Lithuania</option>
                      <option value="Luxembourg">Luxembourg</option>
                      <option value="Madagascar">Madagascar</option>
                      <option value="Malawi">Malawi</option>
                      <option value="Malaysia">Malaysia</option>
                      <option value="Maldives">Maldives</option>
                      <option value="Mali">Mali</option>
                      <option value="Malta">Malta</option>
                      <option value="Marshall Islands">Marshall Islands</option>
                      <option value="Mauritania">Mauritania</option>
                      <option value="Mauritius">Mauritius</option>
                      <option value="Mexico">Mexico</option>
                      <option value="Micronesia">Micronesia</option>
                      <option value="Moldova">Moldova</option>
                      <option value="Monaco">Monaco</option>
                      <option value="Mongolia">Mongolia</option>
                      <option value="Montenegro">Montenegro</option>
                      <option value="Morocco">Morocco</option>
                      <option value="Mozambique">Mozambique</option>
                      <option value="Myanmar">Myanmar</option>
                      <option value="Namibia">Namibia</option>
                      <option value="Nauru">Nauru</option>
                      <option value="Nepal">Nepal</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="New Zealand">New Zealand</option>
                      <option value="Nicaragua">Nicaragua</option>
                      <option value="Niger">Niger</option>
                      <option value="Nigeria">Nigeria</option>
                      <option value="North Korea">North Korea</option>
                      <option value="North Macedonia">North Macedonia</option>
                      <option value="Norway">Norway</option>
                      <option value="Oman">Oman</option>
                      <option value="Pakistan">Pakistan</option>
                      <option value="Palau">Palau</option>
                      <option value="Palestine">Palestine</option>
                      <option value="Panama">Panama</option>
                      <option value="Papua New Guinea">Papua New Guinea</option>
                      <option value="Paraguay">Paraguay</option>
                      <option value="Peru">Peru</option>
                      <option value="Philippines">Philippines</option>
                      <option value="Poland">Poland</option>
                      <option value="Portugal">Portugal</option>
                      <option value="Qatar">Qatar</option>
                      <option value="Romania">Romania</option>
                      <option value="Russia">Russia</option>
                      <option value="Rwanda">Rwanda</option>
                      <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                      <option value="Saint Lucia">Saint Lucia</option>
                      <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                      <option value="Samoa">Samoa</option>
                      <option value="San Marino">San Marino</option>
                      <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                      <option value="Saudi Arabia">Saudi Arabia</option>
                      <option value="Senegal">Senegal</option>
                      <option value="Serbia">Serbia</option>
                      <option value="Seychelles">Seychelles</option>
                      <option value="Sierra Leone">Sierra Leone</option>
                      <option value="Singapore">Singapore</option>
                      <option value="Slovakia">Slovakia</option>
                      <option value="Slovenia">Slovenia</option>
                      <option value="Solomon Islands">Solomon Islands</option>
                      <option value="Somalia">Somalia</option>
                      <option value="South Africa">South Africa</option>
                      <option value="South Korea">South Korea</option>
                      <option value="South Sudan">South Sudan</option>
                      <option value="Spain">Spain</option>
                      <option value="Sri Lanka">Sri Lanka</option>
                      <option value="Sudan">Sudan</option>
                      <option value="Suriname">Suriname</option>
                      <option value="Sweden">Sweden</option>
                      <option value="Switzerland">Switzerland</option>
                      <option value="Syria">Syria</option>
                      <option value="Taiwan">Taiwan</option>
                      <option value="Tajikistan">Tajikistan</option>
                      <option value="Tanzania">Tanzania</option>
                      <option value="Thailand">Thailand</option>
                      <option value="Timor-Leste">Timor-Leste</option>
                      <option value="Togo">Togo</option>
                      <option value="Tonga">Tonga</option>
                      <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                      <option value="Tunisia">Tunisia</option>
                      <option value="Turkey">Turkey</option>
                      <option value="Turkmenistan">Turkmenistan</option>
                      <option value="Tuvalu">Tuvalu</option>
                      <option value="Uganda">Uganda</option>
                      <option value="Ukraine">Ukraine</option>
                      <option value="United Arab Emirates">United Arab Emirates</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                      <option value="Uruguay">Uruguay</option>
                      <option value="Uzbekistan">Uzbekistan</option>
                      <option value="Vanuatu">Vanuatu</option>
                      <option value="Vatican City">Vatican City</option>
                      <option value="Venezuela">Venezuela</option>
                      <option value="Vietnam">Vietnam</option>
                      <option value="Yemen">Yemen</option>
                      <option value="Zambia">Zambia</option>
                      <option value="Zimbabwe">Zimbabwe</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm block mb-2">Number of Chats</label>
                    <input
                      type="number"
                      value={newChats}
                      onChange={(e) => setNewChats(e.target.value)}
                      placeholder="0"
                      min="1"
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      size="sm" 
                      onClick={() => {
                        if (newCountry && newChats) {
                          const current = dashboardData?.popular_countries || [];
                          const chatsToAdd = parseInt(newChats);
                          
                          // Check if country already exists
                          const existingCountryIndex = current.findIndex(country => country.name === newCountry);
                          
                          let updated;
                          if (existingCountryIndex !== -1) {
                            // Country exists - add to existing count
                            updated = [...current];
                            updated[existingCountryIndex] = {
                              ...updated[existingCountryIndex],
                              chats: updated[existingCountryIndex].chats + chatsToAdd
                            };
                            toast({
                              title: "Country Updated",
                              description: `Added ${chatsToAdd} chats to existing ${newCountry} entry`,
                              variant: "success"
                            });
                          } else {
                            // New country - add as new entry
                            updated = [...current, { name: newCountry, chats: chatsToAdd }];
                            toast({
                              title: "Country Added",
                              description: `${newCountry} added with ${chatsToAdd} chats`,
                              variant: "success"
                            });
                          }
                          
                          updateDashboard({ popular_countries: updated });
                          setNewCountry('');
                          setNewChats('');
                        }
                      }}
                      disabled={!newCountry || !newChats || updating}
                      className="bg-violet-600 hover:bg-violet-700"
                    >
                      Add Country
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-600">
            <CardHeader>
              <CardTitle className="text-white">Message Usage (Last 28 days)</CardTitle>
              <CardDescription className="text-gray-400">
                Set message usage for plan quota display
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm block mb-2">Plan Quota</label>
                  <p className="text-white font-medium">
                    {getCurrentPlanQuota().toLocaleString()} messages/month
                  </p>
                </div>
                <div>
                  <label className="text-gray-400 text-sm block mb-2">Current Usage</label>
                  <input
                    type="number"
                    value={messageUsage}
                    onChange={(e) => setMessageUsage(parseInt(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2"
                  />
                </div>
                <Button 
                  size="sm" 
                  onClick={() => updateDashboard({ message_usage: messageUsage })}
                  disabled={updating}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  Update Usage
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-600">
            <CardHeader>
              <CardTitle className="text-white">Today's Users & Messages</CardTitle>
              <CardDescription className="text-gray-400">
                Update today's activity numbers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm block mb-2">Today's Users</label>
                  <input
                    type="number"
                    value={todaysUsers}
                    onChange={(e) => setTodaysUsers(parseInt(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm block mb-2">Today's Messages</label>
                  <input
                    type="number"
                    value={todaysMessages}
                    onChange={(e) => setTodaysMessages(parseInt(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Button 
                  size="sm" 
                  onClick={() => updateDashboard({ todays_users: todaysUsers, todays_messages: todaysMessages })}
                  disabled={updating}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  Update Today's Stats
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ChatbotDetailsModal;